import { initializeApp } from 'firebase/app';
import {
    getAuth,
    GoogleAuthProvider,
    EmailAuthProvider,
    signInWithPopup,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword,
    sendPasswordResetEmail as firebaseSendPasswordResetEmail,
    signOut,
    reauthenticateWithCredential
} from 'firebase/auth';
import {
    initializeFirestore,
    memoryLocalCache,
    collection,
    query,
    where,
    getDocs,
    setDoc,
    doc,
    getDoc,
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { getAnalytics, isSupported } from 'firebase/analytics';
import type { Analytics } from 'firebase/analytics';
import { FirebaseAuth, FirebaseFirestore, FirebaseUser } from '../types/firebase';

console.log('Firebase config:', {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
});

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
console.log('Initializing Firebase...');
const app = initializeApp(firebaseConfig);
console.log('Firebase initialized successfully');

// Initialize Firebase services
console.log('Initializing Firebase services...');
export const auth = getAuth(app) as FirebaseAuth;
/** Memory cache avoids slow/corrupt IndexedDB persistence on some browsers (common source of multi‑second first reads). */
export const db = initializeFirestore(app, { localCache: memoryLocalCache() }) as FirebaseFirestore;
export const storage = getStorage(app);
export const functions = getFunctions(app, 'us-central1');

/** Set when the host supports Firebase Analytics (many embedded WebViews do not). */
let analyticsInstance: Analytics | undefined;
void isSupported()
    .then((supported) => {
        if (!supported) {
            console.log('[TRWidget] Firebase Analytics not supported in this environment');
            return;
        }
        try {
            analyticsInstance = getAnalytics(app);
        } catch (e) {
            console.warn('[TRWidget] Firebase Analytics init failed', e);
        }
    })
    .catch((e) => console.warn('[TRWidget] Firebase Analytics isSupported check failed', e));

export function getFirebaseAnalytics(): Analytics | undefined {
    return analyticsInstance;
}

console.log('Firebase services initialized successfully');

// Initialize Auth Providers
const googleProvider = new GoogleAuthProvider();
const emailProvider = new EmailAuthProvider();

/* REAUTHENTICATE USER
======================================================== */
export const reauthenticateUser = async (currentPassword: string) => {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error('No user logged in');

    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    return reauthenticateWithCredential(user, credential);
}

/* SIGN IN WITH GOOGLE
======================================================== */
export const signInWithGoogle = async () => {
    try {
        const res = await signInWithPopup(auth, googleProvider);
        const user = res.user;
        const userRef = doc(db, 'users', user.uid);
        const existingById = await getDoc(userRef);
        if (existingById.exists()) return;

        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('uid', '==', user.uid));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            await setDoc(userRef, {
                uid: user.uid,
                name: user.displayName,
                authProvider: 'google',
                email: user.email,
            });
        }
    } catch (err) {
        console.error(err);
    }
};

/* SIGN IN WITH EMAIL AND PASSWORD
======================================================== */
export const signInWithEmailAndPassword = async (email: string, password: string) => {
    try {
        const res = await firebaseSignInWithEmailAndPassword(auth, email, password);
        const user = res.user;
        const userRef = doc(db, 'users', user.uid);
        const existingById = await getDoc(userRef);
        if (existingById.exists()) return;

        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('uid', '==', user.uid));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            await setDoc(userRef, {
                uid: user.uid,
                authProvider: 'local',
                email: user.email,
            });
        }
    } catch (err) {
        console.error(err);
    }
};

/* REGISTER WITH EMAIL AND PASSWORD
======================================================== */
export const registerWithEmailAndPassword = async (
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    password: string
) => {
    try {
        const res = await createUserWithEmailAndPassword(auth, email, password);
        const user = res.user;
        await setDoc(doc(db, 'users', user.uid), {
            uid: user.uid,
            firstName,
            lastName,
            phone,
            authProvider: 'local',
            email: user.email,
        });
    } catch (err) {
        console.error(err);
    }
};

/* SEND PASSWORD RESET EMAIL
======================================================== */
export const sendPasswordResetEmail = async (email: string) => {
    try {
        await firebaseSendPasswordResetEmail(auth, email);
    } catch (err) {
        console.error(err);
    }
};

/* LOGOUT
======================================================== */
export const logout = async () => {
    const group = localStorage.getItem('group');
    localStorage.removeItem('course');
    await signOut(auth);
    if (group) {
        localStorage.setItem('group', group);
    }
};